import {
  find,
  head,
  isFunction,
  filter,
  pick,
  some,
  chain,
  uniqBy,
} from 'lodash';
import { IViz } from '../interfaces';
import { IAnyAttribute } from '../../datasets';
import { useOpenVizSelector } from '../../common/redux/selectors/viz-selector.hook';
import styled from '@emotion/styled';
import {
  ISearchableDropdownOption,
  SearchableDropdown,
} from '../../ui/dropdowns/searchable-dropdown';
import { makeDropdownOption } from '../../ui/dropdowns/searchable-dropdown/searchable-dropdown.utils';
import { Tooltip } from '../../components/ui/tooltip';
import { ellipsis } from '../../common/emotion/mixins';
import { CSSProperties } from 'react';

const StyledOverflowSpan = styled.span`
  ${ellipsis()};
`;

const StyledSearchableDropdown = styled(SearchableDropdown)`
  min-width: 10rem;
`;

interface IProps {
  viz?: IViz;
  withoutField?: IAnyAttribute;
  selected?: IAnyAttribute;
  onSelect?: any;
  useAllFields?: boolean;
  classNames?: string[];
  dropDownPrefix?: string;
  headerSx?: CSSProperties;
  popperSx?: CSSProperties;
}

export const FieldsDropDown = ({
  withoutField,
  useAllFields,
  onSelect,
  dropDownPrefix = '',
  classNames,
  selected,
  headerSx = {},
  popperSx = { zIndex: 1050 },
}: IProps) => {
  const viz = useOpenVizSelector();
  const fieldsInViz = getFieldsFromViz(viz, withoutField, useAllFields);
  let fields = [...fieldsInViz.fieldsInPlay];
  const onFieldChange = fieldName => {
    const field = find(fields, { name: fieldName });

    if (isFunction(onSelect)) {
      onSelect(field);
    }
  };

  fields = uniqBy([...fields, ...fieldsInViz.fieldsNotInPlay], 'name');
  const fieldMenuOptions = fields
    .filter(f => !f.hidden)
    .map(({ name: _name, displayName }) =>
      makeDropdownOption(_name, displayName),
    );
  return (
    <StyledSearchableDropdown
      id={`${dropDownPrefix}-fields-dropdown`}
      className={classNames?.join(' ')}
      onSelect={(options: ISearchableDropdownOption[]) => {
        const option = head(options);
        if (option) {
          onFieldChange(option?.value);
        }
      }}
      title={
        <Tooltip
          placement={'top'}
          id='verbose-filter-tooltip'
          title={(selected ?? head(fields))?.displayName}
          arrow
        >
          <StyledOverflowSpan className={'dropdown-title'}>
            {(selected ?? head(fields))?.displayName}
          </StyledOverflowSpan>
        </Tooltip>
      }
      options={fieldMenuOptions}
      headerSx={headerSx}
      popperSx={popperSx}
    />
  );
};

export const getFieldsFromViz = (viz, removeField, useAllFields) => {
  if (useAllFields) {
    return {
      fieldsInPlay: filter(
        viz.dataset?.attributes,
        f => f.name !== removeField?.name && !f.hidden,
      ),
      fieldsNotInPlay: [],
    };
  }
  const fieldsInPlay = chain(viz.layout)
    .values()
    .flatten()
    .reject(pick(removeField, 'name'))
    .value();

  const fieldOptions = viz.dataset.attributes
    .filter(f => {
      return f.name !== removeField?.name;
    })
    .filter(f => {
      return some(fieldsInPlay, pick(f, 'name'));
    });
  return {
    fieldsInPlay,
    fieldsNotInPlay: fieldOptions,
  };
};
